import { Button, Loader } from "@shltelemedicine/shl-ui-lib";
import { useEffect, useState } from "react";
import {
  EcgsTableActionType,
  useEcgsTableContext,
} from "../../context/EcgsTableContext";
import useDebounce from "../../hooks/useDebounce";
import useResponsive from "../../hooks/useResponsive";
import { GetEcgsByUserIdProps } from "../../services/ecgs.service";
import EcgsFilter, { EcgsFilters } from "../Shared/EcgsFilter/EcgsFilter";
import SearchAndFilter from "../Shared/SearchAndFilter/SearchAndFilter";
import Paging from "../Shared/Table/Paging/Paging";
import Legend from "../Tree/Legend/Legend";
import EcgsTable from "./EcgsTable/EcgsTable";
import styles from "./FilterableEcgTable.module.css";
import FiltersDescription from "./FiltersDescription/FiltersDescription";
import { Constants } from "../../constants";

type FilterableEcgTableProps = { itemsPerPage?: number };

const FilterableEcgTable = ({
  itemsPerPage = Constants.TABLE_ITEMS_PER_PAGE,
}: FilterableEcgTableProps) => {
  const {
    state: { loading, tableRef, totalEcgs, loadConfigs },
    actions: { loadEcgs },
    dispatch,
  } = useEcgsTableContext();

  const [search, setSearch] = useState(tableRef?.globalFilter);
  const debouncedSearch = useDebounce(search, 1200);

  const [showFilter, setShowFilter] = useState(false);

  const { isMobile } = useResponsive();
  const searchingOnMobile = isMobile && search;

  useEffect(() => {
    if (!tableRef) {
      return;
    }
    loadWithFilters(loadConfigs.filters!);
  }, [tableRef?.curPage]); // eslint-disable-line

  useEffect(() => {
    if (!tableRef) {
      return;
    }
    tableRef!.curPage = 0; //return to the first page on search
    loadWithFilters(loadConfigs.filters!);
  }, [debouncedSearch]); // eslint-disable-line

  const searchChangeHandler = (text?: string) => {
    setSearch(text);
  };

  const filterHandler = async (formFilter: EcgsFilters) => {
    setShowFilter(false);

    tableRef!.curPage = 0; //return to the first page on filter change
    await loadWithFilters(formFilter);
  };

  const pageAmount = 1;

  const loadWithFilters = async (filters: EcgsFilters) => {
    const configs: Omit<GetEcgsByUserIdProps, "userId"> = {
      pageIndex: tableRef!.curPage,
      pageSize: itemsPerPage,
      pageAmount,
      filters: {
        ...filters,
        search: search,
        fromDate: filters?.fromDate,
        toDate: filters?.toDate,
      },
    };

    dispatch({
      type: EcgsTableActionType.SET_LOAD_CONFIGS,
      payload: configs,
    });

    await loadEcgs(configs);
  };

  const [hasNextPage, setHasNextPage] = useState(false);
  const numberOfFetches = Math.ceil(
    ((tableRef?.curPage ?? 0) + 1) / pageAmount
  );

  return (
    <div className="d-flex flex-column h-100">
      <div className="d-flex flex-column flex-grow-1 overflow-hidden position-relative">
        <header
          className={`${styles.header} app-shadow d-flex align-items-center justify-content-between p-0 p-sm-2`}
        >
          <div
            className={`${styles.searchFilterMobile} d-flex align-items-center justify-content-between`}
          >
            <SearchAndFilter
              className={`font-md ${styles.searchInput}`}
              searchText={search ?? undefined}
              onSearchTextChange={searchChangeHandler}
              toggleFilter={() => setShowFilter((prev) => !prev)}
              filterOpen={showFilter}
              onClearSearch={() => searchChangeHandler()}
              disabled={loading}
            />
            {!searchingOnMobile && (
              <div
                className={`${styles.paging} position-absolute w-auto px-2 px-sm-0`}
              >
                <Paging
                  total={
                    numberOfFetches * pageAmount * itemsPerPage -
                    itemsPerPage * pageAmount +
                    totalEcgs
                  }
                  curPage={tableRef?.curPage ?? 0}
                  itemsPerPage={itemsPerPage}
                  nextDisabled={!hasNextPage || loading}
                  backDisabled={(tableRef?.backDisabled || loading) ?? true}
                  onNext={tableRef?.onNextPage}
                  onBack={tableRef?.onPreviousPage}
                />
              </div>
            )}
          </div>
        </header>
        <div className="flex-grow-1 overflow-hidden">
          {loading && (
            <div className="pt-4">
              <Loader />
            </div>
          )}
          {!loading && showFilter && (
            <div className="bg-white position-absolute z-index-max w-100 app-shadow app-border d-flex justify-content-center py-2">
              <Button
                variant=""
                className={styles.closeFilter}
                onClick={() => setShowFilter((prev) => !prev)}
              >
                &#10006;
              </Button>
              <div className={styles.filterWrapper}>
                <EcgsFilter
                  filters={loadConfigs.filters ?? {}}
                  onFilter={filterHandler}
                />
              </div>
            </div>
          )}
          {!loading && (
            <div className="h-100 d-flex flex-column">
              <FiltersDescription {...loadConfigs.filters} />
              <div className="flex-grow-1 overflow-hidden">
                <EcgsTable
                  itemsPerPage={itemsPerPage}
                  highlightText={search}
                  currentPage={tableRef?.curPage || 0}
                  pageAmount={pageAmount}
                  setHasNextPage={setHasNextPage}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <Legend collapsed={false} />
    </div>
  );
};

export default FilterableEcgTable;
